import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {CustomMinimapStyle} from "../Styles";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * CustomMinimap component used to render a scaled horizontal overview of a scrollable container,
 * including draggable viewport highlighting and scaled visual representation of inner columns.
 *
 * @component CustomMinimap
 *
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.targetRef - Reference to the scrollable target container.
 * @param {string} [props.className=""] - Optional custom class appended to the container.
 * @param {number} [props.height=70] - Height of the minimap component.
 * @param {string} [props.columnClass=""] - Class added to each minimap column.
 *
 * @returns {JSX.Element} CustomMinimap UI element.
 */
export function CustomMinimap({
                                  targetRef,
                                  contentRef = null,
                                  className = "",
                                  height = 70,
                                  columnClass = "",
                              }) {
    const trackRef = useRef(null);

    const [scale, setScale] = useState(1);
    const [viewportLeft, setViewportLeft] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [columns, setColumns] = useState([]);

    const draggingRef = useRef({ active: false, startX: 0, startLeft: 0 });

    const recalc = () => {
        const target = targetRef.current;
        const track = trackRef.current;
        const content = contentRef?.current || target;

        if (!target || !track || !content) return;

        const scrollW = target.scrollWidth;
        const clientW = target.clientWidth;

        const newScale = track.clientWidth / scrollW;

        setScale(newScale);
        setViewportWidth(Math.max(clientW * newScale, 60));
        setViewportLeft(target.scrollLeft * newScale);

        const childColumns = Array.from(content.querySelectorAll('[id^="col_"]'));

        const count = (childColumns.length -1 ) || 1;
        const fixedWidth = track.clientWidth / count;

        const mapped = childColumns.map((_, index) => ({
            x: index * fixedWidth,
            w: fixedWidth,
        }));

        setColumns(mapped);
    };

    useLayoutEffect(() => {
        recalc();
    }, []);

    useEffect(() => {
        const target = targetRef.current;
        if (!target) return;

        const onScroll = () => {
            const track = trackRef.current;
            if (!track) return;

            const localScale = track.clientWidth / target.scrollWidth;
            setViewportLeft(target.scrollLeft * localScale);
            setViewportWidth(Math.max(target.clientWidth * localScale, 10));
        };

        const ro = new ResizeObserver(recalc);
        ro.observe(target);
        ro.observe(document.documentElement);

        target.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", recalc);

        return () => {
            target.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", recalc);
            ro.disconnect();
        };
    }, []);

    const onMouseDown = (e) => {
        draggingRef.current = {
            active: true,
            startX: e.clientX,
            startLeft: viewportLeft,
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp, { once: true });

        e.preventDefault();
    };

    const onMove = (e) => {
        const { active, startX, startLeft } = draggingRef.current;
        if (!active) return;

        const delta = e.clientX - startX;
        const track = trackRef.current;
        const target = targetRef.current;

        const maxLeft = track.clientWidth - viewportWidth;
        const next = Math.max(0, Math.min(maxLeft, startLeft + delta));

        setViewportLeft(next);
        target.scrollLeft = next / scale;
    };

    const onUp = () => {
        draggingRef.current.active = false;
        window.removeEventListener("mousemove", onMove);
    };

    return (
        <div className="minimap-container" style={CustomMinimapStyle("container", { height })}>
            <div ref={trackRef} className="minimap-track">

                {columns.map((col, i) => (
                    <div key={i} className="minimap-column" style={CustomMinimapStyle("column", { x: col.x, width: 30 })}/>
                ))}

                <div className="minimap-column-highlight" onMouseDown={onMouseDown} style={CustomMinimapStyle("highlight", { left: viewportLeft, width: viewportWidth })}/>

            </div>
        </div>
    );
}
